import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Clock } from 'lucide-react';
import { useT } from '@/i18n/I18nProvider';
import { Button, Dialog, DialogContent } from '@/shared/ui';
import { useSignOut } from './useSession';

const IDLE_LIMIT_MS = 30 * 60 * 1000;
const WARN_BEFORE_MS = 60 * 1000;
const THROTTLE_MS = 5_000;
const LS_KEY = 'nexops-last-activity';
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

/**
 * Signs the user out after 30 min of inactivity (warning 60 s before).
 * Activity is shared across tabs via localStorage so work in one tab keeps all tabs alive.
 * Mounted only inside the authenticated layout.
 */
export function IdleLogout() {
  const t = useT();
  const signOut = useSignOut();
  const [warn, setWarn] = useState(false);
  const lastActivity = useRef(Date.now());
  const lastWrite = useRef(0);
  const timers = useRef<{ warn?: number; logout?: number }>({});

  const schedule = useCallback(() => {
    window.clearTimeout(timers.current.warn);
    window.clearTimeout(timers.current.logout);
    const elapsed = Date.now() - lastActivity.current;
    timers.current.warn = window.setTimeout(() => setWarn(true), Math.max(0, IDLE_LIMIT_MS - WARN_BEFORE_MS - elapsed));
    timers.current.logout = window.setTimeout(() => {
      setWarn(false);
      void signOut('idle');
    }, Math.max(0, IDLE_LIMIT_MS - elapsed));
  }, [signOut]);

  const recordActivity = useCallback(
    (force = false) => {
      const now = Date.now();
      if (!force && now - lastWrite.current < THROTTLE_MS) return;
      lastWrite.current = now;
      lastActivity.current = now;
      setWarn(false);
      try {
        localStorage.setItem(LS_KEY, String(now));
      } catch {
        /* private mode */
      }
      schedule();
    },
    [schedule],
  );

  useEffect(() => {
    try {
      const shared = Number(localStorage.getItem(LS_KEY) ?? 0);
      if (shared > lastActivity.current) lastActivity.current = shared;
    } catch {
      /* ignore */
    }
    schedule();

    const onAny = () => recordActivity();
    const onVisible = () => !document.hidden && recordActivity(true);
    const onStorage = (e: StorageEvent) => {
      const ts = Number(e.newValue ?? 0);
      if (e.key === LS_KEY && ts > lastActivity.current) {
        lastActivity.current = ts;
        setWarn(false);
        schedule();
      }
    };
    EVENTS.forEach((e) => window.addEventListener(e, onAny, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    const current = timers.current;
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onAny));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
      window.clearTimeout(current.warn);
      window.clearTimeout(current.logout);
    };
  }, [schedule, recordActivity]);

  return (
    <Dialog open={warn} onOpenChange={(open) => !open && recordActivity(true)}>
      {warn && (
        <DialogContent
          title={
            <span className="flex items-center gap-2">
              <Clock className="size-4 text-warning" />
              {t('auth.idleTitle')}
            </span>
          }
          hideClose
          footer={
            <>
              <Button variant="ghost" onClick={() => void signOut('logout')}>
                {t('auth.logoutNow')}
              </Button>
              <Button variant="primary" autoFocus onClick={() => recordActivity(true)}>
                {t('auth.stayLoggedIn')}
              </Button>
            </>
          }
        >
          <Countdown render={(s) => <p className="text-[13px] text-muted-foreground">{t('auth.idleBody', { seconds: s })}</p>} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function Countdown({ render }: { render: (seconds: number) => ReactNode }) {
  const [secs, setSecs] = useState(Math.round(WARN_BEFORE_MS / 1000));
  useEffect(() => {
    const id = window.setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, []);
  return <>{render(secs)}</>;
}
